import React from 'react'
import WarningIcon from '@mui/icons-material/WarningAmberOutlined'
import ErrorIcon from '@mui/icons-material/ErrorOutlineOutlined'
import { ModalConfig } from '../providers'
import { SubmitProps } from '../components/manage-form/manage-form'
import { convertSnakeCaseToCamelCase, normalizePayloadString } from './string'

type RequiredField = {
	credentialType: string,
	fieldName: string,
}

type FieldProperties = {
	label: string,
	placeholder: string,
	masked?: boolean,
}

export const OAUTH_AUTH_TYPE = 'oauth2'

export const getLabelForFieldName = ( fieldName: string, defaultLabel?: string ): string => {
	if ( fieldName in fieldConfig ) {
		return fieldConfig[ fieldName ].label
	}
	return defaultLabel ?? fieldName
}

export const fieldConfig: Record<string, FieldProperties> = {
	client_id: { label: 'Client ID', placeholder: 'Enter your client ID', masked: false },
	client_secret: { label: 'Client Secret', placeholder: 'Enter your client secret', masked: true },
	api_key: { label: 'API Key', placeholder: 'Enter your API key', masked: false },
	username: { label: 'Username', placeholder: 'Enter your username', masked: false },
	password: { label: 'Password', placeholder: 'Enter your password', masked: true },
}

export type ErrorType = 'ERROR' | 'INVALID_CREDENTIALS' | 'DUPLICATE_CREDENTIALS' | 'GENERIC_ERROR'

export const ERROR_TYPES: readonly ErrorType[] = [ 'ERROR', 'INVALID_CREDENTIALS', 'DUPLICATE_CREDENTIALS' ] as const

export const responseModalConfigs: Record<ErrorType, ModalConfig> = {
	// Define your modal configurations here
	ERROR: {
		title: 'Connection Error',
		icon: <ErrorIcon color="error" />,
		headerText: 'Connection Error',
		content: 'We ran into an error connecting with this integration. We will keep trying to connect. Check back later.',
		buttons: [ { label: 'Dismiss', variant: 'contained', color: 'primary' } ],
	},
	INVALID_CREDENTIALS: {
		title: 'Authentication Error',
		icon: <ErrorIcon color="error" />,
		headerText: 'Authentication Error',
		content: 'Your credentials were not accepted by the OEM, and we were unable to establish a connection. Please check that you entered your credentials correctly. If you\'re still having trouble, please contact support.',
		buttons: [ { label: 'Dismiss', variant: 'contained', color: 'primary' } ],
	},
	DUPLICATE_CREDENTIALS: {
		title: 'Integration Already Exists',
		icon: <WarningIcon color="warning" />,
		headerText: 'Integration Already Exists',
		content: 'You already have an integration that uses these AEMP credentials. If you would like to add a new integration for this OEM, please enter a different set of AEMP credentials.',
		buttons: [ { label: 'Dismiss', variant: 'contained', color: 'primary' } ],
	},
	GENERIC_ERROR: {
		title: 'An Error Occurred',
		icon: <ErrorIcon color="error" />,
		headerText: 'An Error Occurred',
		content: 'An unexpected error occurred. Please check your connection and try again. If you continue to see this error, please contact support.',
		buttons: [ { label: 'Dismiss', variant: 'contained', color: 'primary' } ],
	},
}
export const isValidErrorType = ( errorType: string ): errorType is ErrorType => {
	return errorType in responseModalConfigs
}

export const formatFeedInput = ( formData: SubmitProps, requiredFields: RequiredField[] ) => {
	if ( requiredFields.length === 0 ) {
		return {}
	}

	const groupedByCredentialType = requiredFields.reduce( ( accumulator, field ) => {
		const { credentialType, fieldName } = field
		const formValue = formData[ fieldName ]
		const isFormValueEmpty = ( value: string | undefined | null ): boolean => {
			return value === undefined || value === null || ( typeof value === 'string' && value.trim() === '' )
		}

		if ( isFormValueEmpty( formValue ) ) {
			return accumulator
		}

		// Initialize the credentialType object if it doesn't exist
		if ( !( credentialType in accumulator ) ) {
			accumulator[ credentialType ] = {}
		}

		// Convert fieldName from snake_case to camelCase for the API; normalize to remove line returns etc.
		const camelCaseFieldName = convertSnakeCaseToCamelCase( fieldName )
		accumulator[ credentialType ][ camelCaseFieldName ] = normalizePayloadString( formValue )

		return accumulator
	}, {} as Record<string, Record<string, string>> )

	return groupedByCredentialType
}

export const removeExpiresAtFromRequiredFields = <T extends { fieldName: string }>( requiredFields: T[] ) => {
	return requiredFields.filter( ( field ) => field.fieldName !== 'expires_at' )
}
